/**
 * Conteúdo do modal "Novidades do Sistema" (o pop-up de o-que-mudou).
 *
 * COMO ADICIONAR UMA NOVA ATUALIZAÇÃO:
 *  1. Adicione um novo objeto no TOPO do array `NOVIDADES` (mais recente primeiro).
 *  2. Dê a ele uma `versao` NOVA e única (ex.: "2026.08").
 *     → Só de mudar a versão, o pop-up volta a aparecer para todos os usuários,
 *       mesmo para quem já tinha marcado "não mostrar novamente".
 *  3. Em `icone`, use uma das chaves de `IconeNovidade` (mapeadas para ícones
 *     lucide no NovidadesModal). Se precisar de um ícone novo, adicione a chave lá.
 *  4. Escreva com linguagem simples e direta. Cada item = uma melhoria.
 *
 * Nada aqui é técnico: é o que o usuário final lê.
 */

/** Chaves de ícone (lucide) suportadas — o mapeamento fica no NovidadesModal. */
export type IconeNovidade =
  | "teclado"
  | "salvar"
  | "lembrete"
  | "visto"
  | "notificacoes"
  | "duplicar"
  | "status"
  | "reenvio"
  | "imposto"
  | "janela"
  | "rateio"
  | "formulas"
  | "clausula"
  | "anexo"
  | "observacoes"
  | "rotulos";

export interface NovidadeItem {
  icone: IconeNovidade;
  titulo: string;
  descricao: string;
}

export interface Novidade {
  /** Identificador da versão. Trocar isto faz o pop-up reaparecer. */
  versao: string;
  /** Data amigável exibida no cabeçalho da versão. */
  data: string;
  /** Chamada da versão. */
  titulo: string;
  itens: NovidadeItem[];
}

/** Lista de atualizações — SEMPRE a mais recente no topo. */
export const NOVIDADES: Novidade[] = [
  {
    versao: "2026.10",
    data: "Setembro de 2026",
    titulo: "Menos retrabalho na montagem da planilha",
    itens: [
      {
        icone: "duplicar",
        titulo: "Duplicar uma planilha inteira",
        descricao:
          "Evento que se repete agora não precisa ser montado de novo. No menu de três pontinhos da listagem, clique em Duplicar: você confirma cliente, evento, datas e cidade, e a cópia nasce com todos os itens e fornecedores já organizados. Valores e propostas não vêm — cada linha fica pronta para pedir cotação de novo.",
      },
      {
        icone: "reenvio",
        titulo: "Reenviar o pedido completo a um fornecedor",
        descricao:
          "Incluiu ou excluiu um item depois de já ter pedido cotação? Em Solicitar Orçamento agora aparecem todos os fornecedores da planilha, e você reenvia o pedido com todos os itens de uma vez. O fornecedor recebe um único link e devolve um único documento, em vez de dois pedidos separados.",
      },
      {
        icone: "status",
        titulo: "Status que se atualiza sozinho",
        descricao:
          "A coluna Status deixou de ficar sempre em 'Em Andamento'. Ela anda junto com o trabalho: vira 'Enviado para o Wrike' quando a OC é criada e 'FACITE Gerada' quando você gera o pacote. E dá para clicar na etiqueta e mudar na mão a qualquer momento — inclusive marcar o projeto como Concluído.",
      },
      {
        icone: "imposto",
        titulo: "Cálculo do imposto corrigido",
        descricao:
          "O imposto era somado por fora do custo, e o correto é por dentro: a alíquota de 14,25% incide sobre o valor final, não sobre o custo. Isso significa que o imposto e o Total Geral do Evento ficaram mais altos do que antes — agora batendo com o valor que de fato vai para a AS. O mesmo número vale na tela, no Excel e no FACITE.",
      },
      {
        icone: "rateio",
        titulo: "Rateio de complementares por percentual",
        descricao:
          "Em Equipamentos, Materiais e Serviços agora dá para digitar o percentual sobre o total de Serviços Complementares, e o valor em reais aparece calculado ao lado. Melhor: ele se atualiza sozinho conforme as cotações mudam o total, então o rateio não envelhece mais. Se preferir travar um valor fixo, digite direto no campo em reais.",
      },
      {
        icone: "formulas",
        titulo: "Excel exportado com as contas montadas",
        descricao:
          "O resumo do Excel deixou de sair só com números. Subtotal, Essenciais, Complementares, impostos, honorários, taxa e os totais saem como fórmulas ligadas umas às outras. Mude a quantidade de um item na planilha e tudo recalcula até o Total Geral do Evento — dá para conferir e simular sem precisar refazer nada à mão.",
      },
      {
        icone: "clausula",
        titulo: "Cláusula de cancelamento em campo próprio",
        descricao:
          "No formulário que o fornecedor preenche, a cláusula de cancelamento e as observações dividiam um campo só, e no documento final virava um bloco confuso. Agora são dois campos separados, e a cláusula já chega com um texto sugerido para o fornecedor só ajustar o prazo e o percentual.",
      },
      {
        icone: "anexo",
        titulo: "Anexo de Taxas e Seguros voltou a funcionar",
        descricao:
          "Anexar arquivo numa linha recém-criada dava erro sem explicação. Agora a planilha é salva automaticamente antes do envio, e o arquivo sobe normalmente na primeira tentativa.",
      },
      {
        icone: "observacoes",
        titulo: "Observações editáveis em Taxas e Seguros",
        descricao:
          "As linhas de Taxas e Seguros tinham a coluna Observações travada, sem nenhum aviso de que não dava para escrever ali. Agora é um campo editável como nas outras linhas.",
      },
      {
        icone: "rotulos",
        titulo: "Botões de cotação com nome do que fazem",
        descricao:
          'Dois botões diferentes se chamavam quase igual. O de cima virou "Solicitar cotação em blocos" (vários fornecedores de uma vez) e o da linha do item virou "Solicitar cotação do item" (todos os fornecedores daquele item).',
      },
      {
        icone: "janela",
        titulo: "Janelas que caibam na sua tela",
        descricao:
          "Em telas mais baixas, os botões de confirmar podiam ficar escondidos abaixo do fim da janela. Agora eles ficam sempre fixos e visíveis, e só o conteúdo rola.",
      },
    ],
  },
  {
    versao: "2026.07.2",
    data: "Julho de 2026",
    titulo: "Novidades pra facilitar o seu dia",
    itens: [
      {
        icone: "teclado",
        titulo: "Atalhos de teclado no editor",
        descricao:
          "Monte a planilha voando: Ctrl + S (ou ⌘ + S no Mac) salva sem tirar a mão do teclado. Dentro do editor, aperte ? para ver todos os atalhos disponíveis.",
      },
      {
        icone: "salvar",
        titulo: "Nunca mais perca seu trabalho",
        descricao:
          "Chega do 'será que salvei?'. Um indicador mostra se está tudo salvo ou se ficou algo pra trás — e, se você tentar sair sem salvar, a gente avisa antes. Seu trabalho fica protegido.",
      },
      {
        icone: "lembrete",
        titulo: "Lembrar o fornecedor com 1 clique",
        descricao:
          "Fornecedor sumiu sem responder? Um clique envia um e-mail de lembrete simpático na hora. Pra não incomodar, enviamos no máximo um por dia para cada cotação.",
      },
      {
        icone: "visto",
        titulo: "Veja quando o fornecedor abriu o link",
        descricao:
          "Agora dá pra saber se o fornecedor abriu a cotação: passe o mouse no ícone de olho na linha dele e veja 'aberto há 2 dias' ou 'nunca aberto'. Fica fácil decidir quem cobrar.",
      },
      {
        icone: "notificacoes",
        titulo: "Central de notificações",
        descricao:
          "Um sininho no topo avisa na hora quando um fornecedor responde, recusa uma cotação ou quando uma OC é criada — com um feed de tudo que acontece na plataforma. Nada mais se perde no meio dos e-mails.",
      },
    ],
  },
];

/** Versão mais recente — usada para decidir se o pop-up deve reaparecer. */
export const VERSAO_ATUAL: string = NOVIDADES[0]?.versao ?? "0";
